import React, {useState, useMemo, useCallback} from 'react'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import {useIntl, FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {Box, Flex, HStack, IconButton, SimpleGrid, useBreakpointValue} from '@chakra-ui/react'
import ProductTile, {Skeleton as ProductTileSkeleton} from '@app/components/product-tile'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import Carousel from '@app/components/carousel'
import {ChevronRightIcon, ChevronLeftIcon} from '@app/components/custom-icons/index.jsx'
import {useToast} from '@app/hooks/use-toast'
import {useWishlistActions} from '@app/hooks/use-wishlist-actions'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer.js'
import breakpoints from '@app/theme/foundations/breakpoints'
import ProductScroller from '../product-scroller'
import {ArrowButton} from '../arrow-button'

/**
 * ProductCarousel is a versatile React component for displaying a collection of products in a carousel, grid, or slider format.
 *
 * This component supports a wide range of customization options, including the ability to show or hide product images, names, prices, and color variations. It integrates with a wishlist system, allowing users to add or remove products from their wishlist, and supports both static and dynamic layouts based on the number of products and the specified display mode.
 *
 * The carousel adapts responsively to different screen sizes, with configurable slide counts per breakpoint, and supports both standard and "complete the look" product tiles. It also provides options for custom carousel settings, arrow placement, and tile sizing ratios.
 *
 * Key Features:
 * - Displays products as a carousel, grid, or slider depending on props and product count.
 * - Integrates with wishlist functionality, allowing toggling of favorite status.
 * - Supports responsive design with customizable slide counts and tile ratios.
 * - Allows for the inclusion of additional UI elements such as navigation arrows, headers, and "complete the look" overlays.
 * - Handles loading states with skeleton placeholders.
 *
 * Props:
 * @param {string|ReactNode} title - The title displayed above the carousel.
 * @param {number|Object} nbSlidesToShow - Number of slides to show at once, or an object mapping breakpoints to slide counts.
 * @param {boolean} [showProductImages=true] - Whether to display product images.
 * @param {boolean} [showProductNames=true] - Whether to display product names.
 * @param {boolean} [showProductPrices=true] - Whether to display product prices.
 * @param {boolean} [showProductColorVariations=true] - Whether to display product color variations.
 * @param {Array<Object>} products - Array of product objects to display.
 * @param {boolean} isLoading - Whether the carousel is in a loading state.
 * @param {boolean} [isDisabled=false] - Disables carousel navigation and interactions if true.
 * @param {boolean} [isSwitchedImages=false] - Whether to switch main and hover images.
 * @param {Object} headerProps - Additional props for the header container.
 * @param {boolean} isCompleteTheLook - Enables "complete the look" overlays on product tiles.
 * @param {boolean} isSlider - If true, uses a slider layout instead of a carousel.
 * @param {string} recommender - Optional recommender system identifier.
 * @param {string} mainImageType - Type of main product image to display.
 * @param {string} hoverImageType - Type of hover product image to display.
 * @param {boolean} [isFavIconInTitle=true] - Whether to display the favorite icon in the product title.
 * @param {Object} [carouselSettings={}] - Additional settings for the carousel component.
 * @param {boolean} [isArrowsNextToTitle=true] - Places navigation arrows next to the title if true.
 * @param {boolean} [displayPriority=false] - Controls image lazy loading priority.
 * @param {number} smallTileRatio - Ratio for small tiles in mixed-size layouts.
 * @param {...Object} props - Additional props passed to the root container.
 *
 * @returns {JSX.Element} The rendered product carousel component.
 *
 * @example
 * <ProductCarousel
 *   title="Featured Products"
 *   nbSlidesToShow={{ base: 1, md: 3 }}
 *   products={productList}
 *   isLoading={false}
 * />
 */
export const ProductCarousel = ({
    title,
    nbSlidesToShow,
    showProductImages = true,
    showProductNames = true,
    showProductPrices = true,
    showProductColorVariations = true,
    products,
    isLoading,
    isDisabled = false,
    isSwitchedImages = false,
    headerProps,
    isCompleteTheLook,
    isSlider,
    recommender,
    mainImageType,
    hoverImageType,
    isFavIconInTitle = true,
    carouselSettings: propCarouselSettings = {},
    isArrowsNextToTitle = true,
    displayPriority = false,
    smallTileRatio,
    tileTitleHeading,
    ...props
}) => {
    const [swiper, setSwiper] = useState()
    const {wishlist, addProductToWishlist, removeProductFromWishlist} = useWishlistActions()
    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )
    const toast = useToast()
    const {formatMessage} = useIntl()
    const {data: customer} = useCurrentCustomer()
    const {customerId} = customer

    const isMobile = useBreakpointValue({base: true, md: false})
    const nbSlides = useBreakpointValue(
        typeof nbSlidesToShow === 'object' ? nbSlidesToShow : {base: nbSlidesToShow}
    )

    // Determine if the number of tiles is even
    const isEvenTiles = products?.length % 2 === 0

    const carouselSettings = useMemo(
        () => ({
            dots: false,
            arrows: !isDisabled && !isArrowsNextToTitle,
            infinite: true,
            speed: 500,
            slidesToShow: nbSlides || 4,
            swipe: !isDisabled,
            prevArrow: <ArrowButton direction="prev" onClick={() => swiper?.slickPrev()} />,
            nextArrow: <ArrowButton direction="next" onClick={() => swiper?.slickNext()} />,
            variableWidth: smallTileRatio ? true : false,
            ...propCarouselSettings
        }),
        [nbSlides, isDisabled, propCarouselSettings, isArrowsNextToTitle]
    )

    /**
     * Toggles the wishlist status of a given product for a customer.
     *
     * This function checks if the specified product is already present in the customer's wishlist.
     * If the product is found, it removes the product from the wishlist. Otherwise, it adds the product
     * to the wishlist. The function utilizes asynchronous operations to handle the addition or removal
     * of the product from the wishlist.
     *
     * @function
     * @param {Object} product - The product object to be toggled in the wishlist.
     * @param {string} product.id - The unique identifier of the product.
     *
     * @returns {Promise<void>} A promise that resolves when the wishlist operation is complete.
     *
     * @dependencies
     * - wishlist: The current state of the customer's wishlist.
     * - customerId: The unique identifier of the customer.
     * - removeProductFromWishlist: Function to remove an item from the wishlist.
     * - addProductToWishlist: Function to add an item to the wishlist.
     * - deleteCustomerProductListItem: Function to delete a product list item from the customer's wishlist.
     * - createCustomerProductListItem: Function to create a product list item in the customer's wishlist.
     * - toast: Function to display notifications to the user.
     * - formatMessage: Function to format messages for display.
     */
    const handleWishlistToggle = useCallback(
        async (product) => {
            if (
                wishlist?.customerProductListItems?.some((item) => item.productId === product?.id)
            ) {
                await removeProductFromWishlist(product)
            } else {
                await addProductToWishlist(product)
            }
        },
        [
            wishlist,
            customerId,
            removeProductFromWishlist,
            addProductToWishlist,
            deleteCustomerProductListItem,
            createCustomerProductListItem,
            toast,
            formatMessage
        ]
    )

    /**
     * Calculates the widths of slides to be displayed in a carousel based on the number of slides to show.
     * Utilizes the `useMemo` hook to optimize performance by memoizing the calculated widths.
     *
     * @param {number|Object} nbSlidesToShow - The number of slides to display at once.
     *        If a number is provided, it represents the fixed number of slides to show.
     *        If an object is provided, it should map breakpoints to the number of slides to show at each breakpoint.
     *
     * @returns {Array<string>} An array of strings representing the widths in viewport width units ('vw').
     *        If `nbSlidesToShow` is not provided, returns an array with a single element '100vw'.
     *        If `nbSlidesToShow` is an object, calculates widths for each breakpoint based on the provided values.
     *        If `nbSlidesToShow` is a number, calculates a single width value.
     */
    const calculateWidths = useMemo(
        () => (nbSlidesToShow, index) => {
            if (!nbSlidesToShow) {
                return ['100vw']
            }

            const largeTileRatio = 1 // Full width for the large tile

            if (typeof nbSlidesToShow === 'object') {
                const breakpointsKeys = Object.keys(breakpoints)
                let lastDefinedValue = null

                return breakpointsKeys.reduce((acc, breakpoint) => {
                    if (nbSlidesToShow[breakpoint] !== undefined) {
                        // Calculate the total ratio based on the number of slides
                        const slidesToShow = nbSlidesToShow[breakpoint]
                        let totalRatio = 0
                        if (smallTileRatio) {
                            for (let i = 0; i < slidesToShow; i++) {
                                totalRatio += i % 2 === 0 ? largeTileRatio : smallTileRatio
                            }
                        } else {
                            smallTileRatio = 1
                            totalRatio = 1
                        }

                        // Calculate the width for the current index
                        lastDefinedValue = `${Math.round(
                            100 *
                                (index % 2 === 0
                                    ? largeTileRatio / totalRatio
                                    : smallTileRatio / totalRatio)
                        )}vw`
                    }
                    if (lastDefinedValue) {
                        acc.push(lastDefinedValue)
                    }
                    return acc
                }, [])
            }

            const width = `${Math.round(100 / nbSlidesToShow)}vw`
            return [width]
        },
        [nbSlidesToShow, smallTileRatio]
    )

    const renderProductTile = (product, isCompleteTheLook, onClick, index) => (
        <>
            <ProductTile
                product={product}
                isSwitchedImages={isSwitchedImages}
                isFavourite={wishlist?.customerProductListItems?.some(
                    (item) => item.productId === product?.id
                )}
                onFavouriteToggle={() => {
                    handleWishlistToggle(product)
                }}
                onClick={() => onClick(product)}
                dynamicImageProps={{
                    widths: calculateWidths(nbSlidesToShow, index),
                    imageProps: {
                        width: '100%'
                    }
                }}
                showProductImages={showProductImages}
                showProductNames={showProductNames}
                showProductPrices={showProductPrices}
                showProductColorVariations={showProductColorVariations}
                isFavIconInTitle={isFavIconInTitle}
                isPriceLarge={false}
                mt={5}
                recommender={recommender}
                mainImageType={mainImageType}
                hoverImageType={hoverImageType}
                isImageLazyLoading={!displayPriority}
                titleHeading={tileTitleHeading}
                isCompleteTheLook={isCompleteTheLook}
            />
        </>
    )

    return (
        <Box css={[ProductCarousel.css(smallTileRatio, isEvenTiles, isLoading)]} {...props}>
            <Flex
                flex={1}
                pt={title ? 3 : 0}
                pb={title ? ['35px', null, '29px'] : 0}
                mx={['10px', null, '20px']}
                justifyContent={'space-between'}
                alignItems={'flex-end'}
                {...headerProps}
            >
                {title}
                {isArrowsNextToTitle && !isDisabled && products?.length > nbSlides && !isSlider && (
                    <HStack display={isMobile ? 'none' : 'flex'} gap={6}>
                        <IconButton
                            aria-label={formatMessage({
                                id: 'carousel.button.prev',
                                defaultMessage: 'Previous'
                            })}
                            icon={<ChevronLeftIcon boxSize="12px" />}
                            variant="ghost"
                            onClick={() => {
                                swiper?.slickPrev()
                            }}
                            w={'40px'}
                            h={'40px'}
                            border={'1px solid var(--chakra-colors-gray)'}
                            _focus={{zIndex: 1}}
                        />
                        <IconButton
                            aria-label={formatMessage({
                                id: 'carousel.button.next',
                                defaultMessage: 'Next'
                            })}
                            icon={<ChevronRightIcon boxSize="12px" />}
                            variant="ghost"
                            onClick={() => {
                                swiper?.slickNext()
                            }}
                            w={'40px'}
                            h={'40px'}
                            border={'1px solid var(--chakra-colors-gray)'}
                        />
                    </HStack>
                )}
            </Flex>
            {products?.length <= nbSlides ? (
                <SimpleGrid
                    columns={nbSlides}
                    spacing={10}
                    px={[10, null, 20]}
                    py={1}
                    as="ul"
                    className="grid-list"
                >
                    {products?.map((product, idx) => (
                        <Box
                            key={product?.id || idx}
                            width="100%"
                            as="li"
                            listStyleType="none"
                            position={'relative'}
                            className="grid-tile"
                        >
                            {renderProductTile(product, isCompleteTheLook, props.onClick, idx)}
                        </Box>
                    ))}
                </SimpleGrid>
            ) : isSlider ? (
                <ProductScroller
                    products={products}
                    isLoading={isLoading}
                    recommender={recommender ? recommender : null}
                />
            ) : (
                <Carousel
                    settings={carouselSettings}
                    label={formatMessage({
                        id: 'home.services.push.carousel.label',
                        defaultMessage: 'Home services push carousel'
                    })}
                    setSwiper={setSwiper}
                    nbSlidesToShowByBreakpoint={nbSlidesToShow}
                    smallTileRatio={smallTileRatio}
                >
                    {isLoading
                        ? new Array(6).fill(0).map((_, index) => (
                              <Box key={`skeleton-${index}`} className="grid-tile">
                                  <ProductTileSkeleton p={5} />
                              </Box>
                          ))
                        : products?.map((product, index) => (
                              <Box key={product.id} position={'relative'}>
                                  {renderProductTile(
                                      product,
                                      isCompleteTheLook,
                                      props.onClick,
                                      index
                                  )}
                              </Box>
                          ))}
                </Carousel>
            )}
        </Box>
    )
}

ProductCarousel.css = (smallTileRatio, isEvenTiles, isLoading) => css`
    .slick-slider {
        padding-right: min(calc(44px), calc(100vw / 8));

        @media screen and (min-width: 768px) {
            padding-right: 0;
            margin-left: 15px;
            margin-right: 15px;
        }
    }
    .slick-track {
        display: flex;
    }
    .slick-list {
        overflow: visible;

        @media screen and (min-width: 768px) {
            overflow: hidden;
        }
    }
    .slick-slide {
        height: inherit;
        margin-left: 10px;

        @media screen and (min-width: 768px) {
            margin-left: 5px;
            margin-right: 5px;
        }
    }
    .slick-arrow {
        opacity: 0;
        pointer-events: none;
        position: absolute;
        left: 30px;
        top: 39%;
        width: 40px;
        height: 40px;
        background: white;
        z-index: 1;
        transform: none;
        color: var(--chakra-colors-fullBlack);
        border: 1px solid var(--chakra-colors-gray);
        &::before {
            content: '';
        }
        &:focus {
            opacity: 1;
        }
        @media screen and (min-width: 768px) {
            opacity: 1;
            pointer-events: auto;
        }
    }
    .slick-next {
        left: auto;
        right: 30px;
    }

    ${smallTileRatio &&
    (isEvenTiles || isLoading
        ? `
        .slick-slide:nth-child(even),
        .grid-tile:nth-child(even) {
            flex: ${smallTileRatio};
        }
        `
        : `
        .slick-slide:nth-child(odd),
        .grid-tile:nth-child(odd) {
            flex: ${smallTileRatio};
        }
        `)}
`

ProductCarousel.propTypes = {
    title: PropTypes.object,
    nbSlidesToShow: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    showProductImages: PropTypes.bool,
    showProductNames: PropTypes.bool,
    showProductPrices: PropTypes.bool,
    showProductColorVariations: PropTypes.bool,
    products: PropTypes.array,
    isLoading: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isSwitchedImages: PropTypes.bool,
    isSlider: PropTypes.bool,
    headerProps: PropTypes.object,
    isCompleteTheLook: PropTypes.bool,
    onClick: PropTypes.func,
    recommender: PropTypes.string,
    mainImageType: PropTypes.string,
    hoverImageType: PropTypes.string,
    isFavIconInTitle: PropTypes.bool,
    carouselSettings: PropTypes.object,
    isArrowsNextToTitle: PropTypes.bool,
    displayPriority: PropTypes.bool,
    smallTileRatio: PropTypes.number,
    tileTitleHeading: PropTypes.string
}
